import { createSlice } from "@reduxjs/toolkit";
import questData from "../../questionnaire/store/dataJSON.json";

export interface CategoryOption {
  id: string;
  label: string;
  unit?: string;
  type?: string;
  availableOperators?: string[];
  choices?: Array<{
    index?: number;
    id?: number | number[];
    label: string;
    info?: string;
    lengthUnit?: string;
    massUnit?: string;
  }>;
}

export interface CategoryOptions {
  [key: string]: CategoryOption[];
}

export interface RootStateServiceLogicBuilder {
  serviceLogicBuilder: ServiceLogicBuilderState;
}

export interface ServiceLogicBuilderState {
  categoryOptions: CategoryOptions;
  savedServices: any[];
  savedLogics: any[];
  savedLabPanels: any[];
  // True once the user lab panels have been fetched at least once. Lets the Lab
  // Builder tell "not loaded yet" apart from "loaded but empty" (no custom
  // panels), so it doesn't flash the loader / refetch on back-nav.
  labPanelsLoaded: boolean;
  // Provider (read-only) lab panels cached per provider slug; a present key
  // means that provider's panels have already been loaded.
  providerLabPanels: Record<string, any[]>;
  loading: boolean;
  error: string | null;
  activeLogicTab: number; // 0: PNOE logic, 1: Custom
  activeLabTab: "custom" | "provider"; // persisted so back nav returns to it
}

// Transform Biometric Questionnaire data to CategoryOption format
const transformBiometricData = (): CategoryOption[] => {
  return questData.allData.Biometrics.filter((item: any) => !item._disabled) // Exclude disabled items
    .map((item: any) => ({
      id: item.category,
      label: item.label || item.category, // Fallback to category if label is missing
      type: item.type,
      choices: item.choices || [],
      availableOperators: item.availableOperators,
      // Add unit based on type - can be extended later
      unit:
        item.type === "scale"
          ? item.category.includes("weight")
            ? "kg"
            : item.category.includes("height")
            ? "cm"
            : ""
          : undefined,
    }));
};

// Transform Lifestyle Questionnaire data to CategoryOption format
const transformLifestyleData = (): CategoryOption[] => {
  return questData.allData.Lifestyle.filter((item: any) => !item._disabled) // Exclude disabled items
    .map((item: any) => ({
      id: item.category,
      label: item.label || item.category, // Fallback to category if label is missing
      type: item.type,
      choices: item.choices || [],
      availableOperators: item.availableOperators,
      // No units needed for lifestyle questions as they're mostly categorical
      unit: undefined,
    }));
};

// Transform Nutrition Questionnaire data to CategoryOption format
const transformNutritionData = (): CategoryOption[] => {
  return questData.allData.Nutrition.filter(
    (item: any) =>
      !item._disabled && // Exclude disabled items
      ((item.category && item.category !== "") || item.stepper_category) // Include items with valid categories or stepper_category
  ).map((item: any) => ({
    id: item.stepper_category || item.category, // Use stepper_category if available, otherwise category
    label: item.label || item.stepper_category || item.category, // Fallback hierarchy
    type: item.type,
    choices: item.choices || [],
    availableOperators: item.availableOperators,
    // No units needed for nutrition questions
    unit: undefined,
  }));
};

// Transform Wearable data to CategoryOption format
const transformWearableData = (): CategoryOption[] => {
  return questData.allData.Wearable.map((item: any) => ({
    id: item.category,
    label: item.label,
    unit: item.unit,
    type: item.type,
    availableOperators: undefined, // Standard numeric operators
  })).sort((a: CategoryOption, b: CategoryOption) => a.label.localeCompare(b.label));
};

// Transform API Logic Categories data to CategoryOption format
const transformLogicCategoriesData = (
  logicCategories: any
): CategoryOption[] => {
  if (!logicCategories || !logicCategories.metrics) {
    return [];
  }

  return logicCategories.metrics.map((metric: any) => ({
    id: metric.id,
    label: metric.label,
    unit:
      metric.id === "8_AMR" || metric.id === "8_RMR" ? "high/low" : metric.unit,
    type: "numeric", // Most PNOE metrics are numeric
    availableOperators:
      metric.id === "8_AMR" || metric.id === "8_RMR" ? ["="] : null, // Standard numeric operators
  })).sort((a: CategoryOption, b: CategoryOption) => a.label.localeCompare(b.label));
};

// Transform Lab Work biomarkers data to CategoryOption format
const transformLabWorkData = (biomarkers: any[]): CategoryOption[] => {
  if (!biomarkers || !Array.isArray(biomarkers)) {
    return [];
  }

  return biomarkers.map((biomarker: any) => ({
    id: biomarker.id,
    label: biomarker.name || biomarker.id,
    unit: biomarker.unit || "",
    type: "numeric", // All biomarkers are numeric input
    availableOperators: undefined, // Standard numeric operators
  })).sort((a: CategoryOption, b: CategoryOption) => a.label.localeCompare(b.label));
};

const initialState: ServiceLogicBuilderState = {
  categoryOptions: {
    "Biometric Questionnaire": transformBiometricData(),
    "Lifestyle Questionnaire": transformLifestyleData(),
    "Nutrition Questionnaire": transformNutritionData(),
    "Pnoe Metrics": [],
    "Lab Work": [],
    "Wearable Integration": transformWearableData(),
  },
  savedServices: [],
  savedLogics: [],
  savedLabPanels: [],
  labPanelsLoaded: false,
  providerLabPanels: {},
  loading: false,
  error: null,
  activeLogicTab: 0, // Default to PNOE logic tab
  activeLabTab: "provider",
};

const serviceLogicBuilderSlice = createSlice({
  name: "serviceLogicBuilder",
  initialState,
  reducers: {
    setCategoryOptions(state, action) {
      state.categoryOptions = action.payload;
    },
    updateCategoryOptions(state, action) {
      const { category, options } = action.payload;
      state.categoryOptions[category] = options;
    },
    setSavedServices(state, action) {
      state.savedServices = action.payload;
    },
    setSavedLogics(state, action) {
      state.savedLogics = action.payload;
    },
    setSavedLabPanels(state, action) {
      state.savedLabPanels = action.payload;
      state.labPanelsLoaded = true;
    },
    setProviderLabPanels(state, action) {
      const { provider, panels } = action.payload;
      state.providerLabPanels[provider] = panels;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    setActiveLabTab(state, action) {
      state.activeLabTab = action.payload;
    },
    setActiveLogicTab(state, action) {
      state.activeLogicTab = action.payload;
    },
    updatePnoeMetrics(state, action) {
      state.categoryOptions["Pnoe Metrics"] = transformLogicCategoriesData(
        action.payload
      );
      // Also update Lab Work if biomarkers are present
      if (action.payload.biomarkers) {
        state.categoryOptions["Lab Work"] = transformLabWorkData(
          action.payload.biomarkers
        );
      }
    },
  },
});

export const serviceLogicBuilderActions = serviceLogicBuilderSlice.actions;

export default serviceLogicBuilderSlice;
